/**
 * RBAC-3.7 — the By capability tab (FR-17a, FR-17b, UX-DR12, UX-DR13).
 *
 * Renders `lib/capabilityLookup.ts` and nothing else: what a capability is, who holds it, what a
 * toggle writes and every refusal sentence come from there, tested without a DOM.
 *
 * ⚠️ Toggles edit the SAME per-role draft as the role editor (held by `RolesPermissionsPage`), and
 * nothing here saves. RBAC-3.6 saves on the Roles tab, one role at a time, through its diff; this
 * tab's bar says so rather than claiming saving does not exist.
 * ⚠️ No toggle on a Joules role (FR-5) or a reserved capability (FR-12): a sentence instead.
 */
import { useMemo, useState } from 'react';
import { AlertTriangle, Lock, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { AccountRole } from '@/lib/accountRoles';
import {
  COMPARE_MAX,
  COMPARE_MIN,
  COMPARE_REASON,
  compareRows,
  draftedRoles,
  effectivePermissions,
  filterCapabilities,
  findCapability,
  JOULES_READ_ONLY,
  listCapabilities,
  OVERRIDE_STATE,
  toggleCapability,
  toggleCompare,
  whoHolds,
  type Capability,
  type Drafts,
  type RoleHolding,
} from '@/lib/capabilityLookup';

interface Props {
  roles: AccountRole[];
  drafts: Drafts;
  onDraftChange: (roleId: number, next: string[] | null) => void;
  capabilityId: string | null;
  onSelectCapability: (id: string) => void;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function CapabilityList({ selected, onSelect }: { selected: Capability; onSelect: (id: string) => void }) {
  const [query, setQuery] = useState('');
  const shown = useMemo(() => filterCapabilities(listCapabilities(), query), [query]);
  const areas = [...new Set(shown.map((c) => c.area))];
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search capabilities"
          aria-label="Search capabilities"
          className="pl-8"
        />
      </div>
      <nav aria-label="Capabilities" className="max-h-72 overflow-y-auto md:max-h-[70vh]">
        {areas.length === 0 && <p className="px-2 py-2 text-sm text-muted-foreground">No capability matches.</p>}
        {areas.map((area) => (
          <div key={area}>
            <div className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{area}</div>
            {shown.filter((c) => c.area === area).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onSelect(c.id)}
                aria-current={c.id === selected.id ? 'true' : undefined}
                className={cn(
                  'flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  c.id === selected.id ? 'bg-primary/10 font-medium text-foreground' : 'text-foreground hover:bg-muted',
                )}
              >
                {c.kind === 'locked' && <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
                {c.kind === 'destructive' && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />}
                <span className="min-w-0">{c.label}</span>
              </button>
            ))}
          </div>
        ))}
      </nav>
    </div>
  );
}

function HolderRow({ h, cap, onToggle }: { h: RoleHolding; cap: Capability; onToggle: (on: boolean) => void }) {
  const id = `cap-${cap.id}-${h.role.id}`;
  return (
    <li className="flex items-start gap-3 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <label htmlFor={h.toggle ? id : undefined} className="text-sm font-medium text-foreground">{h.role.name}</label>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-medium',
              h.role.isGlobal ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary',
            )}
          >
            {h.role.isGlobal ? 'Joules role' : 'Your role'}
          </span>
          {h.changed && (
            <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">Unsaved</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {plural(h.role.holderCount, 'person', 'people')}
          {/* The group already says has / has not; the state adds only a rung, a reach or the override. */}
          {h.holds && (cap.kind === 'rung' || h.state === OVERRIDE_STATE) && ` · ${h.state}`}
        </p>
        {/* Joules roles share one sentence above the lists; any other reason is the row's own. */}
        {h.reason && h.reason !== JOULES_READ_ONLY && <p className="mt-0.5 text-xs text-muted-foreground">{h.reason}</p>}
      </div>
      {h.toggle ? (
        <Switch
          id={id}
          checked={h.toggle === 'on'}
          onCheckedChange={onToggle}
          className={cn('mt-0.5 shrink-0', cap.kind === 'destructive' && 'data-[state=checked]:bg-destructive')}
        />
      ) : (
        <Lock className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" aria-label={h.reason ?? 'No toggle'} />
      )}
    </li>
  );
}

function HolderGroup({
  title,
  rows,
  cap,
  onToggle,
}: {
  title: string;
  rows: RoleHolding[];
  cap: Capability;
  onToggle: (h: RoleHolding, on: boolean) => void;
}) {
  return (
    <section aria-label={title}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title} · {plural(rows.length, 'role', 'roles')}
      </h3>
      {rows.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">None.</p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((h) => (
            <HolderRow key={h.role.id} h={h} cap={cap} onToggle={(on) => onToggle(h, on)} />
          ))}
        </ul>
      )}
    </section>
  );
}

function Compare({ roles, drafts }: { roles: AccountRole[]; drafts: Drafts }) {
  const [selected, setSelected] = useState<number[]>([]);
  const [onlyDiffering, setOnlyDiffering] = useState(false);
  const chosen = selected.map((id) => roles.find((r) => r.id === id)).filter((r): r is AccountRole => !!r);
  const full = chosen.length >= COMPARE_MAX;
  const ready = chosen.length >= COMPARE_MIN;
  const areas = ready ? compareRows(chosen, drafts) : [];

  return (
    <section aria-label="Compare side by side" className="space-y-3 rounded-md border border-border p-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Compare side by side</h3>
        <p className="text-sm text-muted-foreground">{COMPARE_REASON}</p>
      </div>
      <fieldset>
        <legend className="sr-only">Roles to compare</legend>
        <div className="flex flex-wrap gap-2">
          {roles.map((r) => {
            const on = selected.includes(r.id);
            const disabled = !on && full;
            return (
              <label
                key={r.id}
                className={cn(
                  'flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm',
                  on ? 'border-primary bg-primary/5' : 'border-border',
                  disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                )}
              >
                <Checkbox checked={on} disabled={disabled} onCheckedChange={() => setSelected((s) => toggleCompare(s, r.id))} />
                {r.name}
              </label>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground" aria-live="polite">
          {full
            ? `${COMPARE_MAX} chosen, which is the most. Untick one to choose another.`
            : ready
              ? `${chosen.length} chosen. You can add one more.`
              : `Choose ${COMPARE_MIN - chosen.length} more.`}
        </p>
      </fieldset>

      {ready && (
        <>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <Checkbox checked={onlyDiffering} onCheckedChange={(v) => setOnlyDiffering(v === true)} />
            Only where they differ
          </label>
          <table className="w-full table-fixed border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="w-[34%] py-2 pr-2 font-medium text-muted-foreground">Capability</th>
                {chosen.map((r) => (
                  <th key={r.id} className="py-2 pr-2 font-medium text-foreground break-words">{r.name}</th>
                ))}
              </tr>
            </thead>
            {areas.map((a) => {
              const rows = onlyDiffering ? a.rows.filter((r) => r.differs) : a.rows;
              if (rows.length === 0) return null;
              return (
                <tbody key={a.area}>
                  <tr>
                    <th colSpan={chosen.length + 1} className="pb-1 pt-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {a.area}
                    </th>
                  </tr>
                  {rows.map((row) => (
                    <tr key={row.label} className={cn('border-b border-border align-top', row.differs && 'bg-muted/40')}>
                      <td className="py-1.5 pr-2 text-foreground break-words">{row.label}</td>
                      {row.cells.map((c, i) => (
                        <td key={chosen[i].id} className="py-1.5 pr-2 text-muted-foreground break-words">{c}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              );
            })}
          </table>
        </>
      )}
    </section>
  );
}

export function CapabilityLookup({ roles, drafts, onDraftChange, capabilityId, onSelectCapability }: Props) {
  const cap = findCapability(capabilityId) ?? listCapabilities()[0];
  const result = useMemo(() => whoHolds(cap, roles, drafts), [cap, roles, drafts]);
  const drafted = draftedRoles(roles, drafts);

  const toggle = (h: RoleHolding, on: boolean) => {
    const next = toggleCapability(effectivePermissions(h.role, drafts), cap, on);
    onDraftChange(h.role.id, next);
  };

  return (
    <div className="grid md:grid-cols-[280px_minmax(0,1fr)]">
      <div className="border-b border-border p-3 md:border-b-0 md:border-r">
        <CapabilityList selected={cap} onSelect={onSelectCapability} />
      </div>
      <div className="min-w-0 space-y-5 p-6">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-foreground">{cap.label}</h2>
          <p className="text-sm text-muted-foreground">{cap.description}</p>
          {cap.irreversible && <p className="text-sm text-destructive">{cap.irreversible}</p>}
          <p className="pt-1 text-sm font-medium text-foreground">{result.summary}</p>
          <p className="text-xs text-muted-foreground">
            People are counted per role. Someone who holds two roles is counted in each, so the counts do not add up to a total.
          </p>
        </div>

        {drafted.length > 0 && (
          <div
            role="status"
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm"
          >
            <span className="text-foreground">
              <span className="font-medium">Unsaved changes to {plural(drafted.length, 'role', 'roles')}: {drafted.map((r) => r.name).join(', ')}.</span>{' '}
              <span className="text-muted-foreground">Nothing is saved yet. Open each role on the Roles tab to review who the change affects and save it.</span>
            </span>
            <Button variant="outline" size="sm" onClick={() => drafted.forEach((r) => onDraftChange(r.id, null))}>
              Discard
            </Button>
          </div>
        )}

        {roles.some((r) => r.isGlobal) && (
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{JOULES_READ_ONLY}</span>
          </p>
        )}

        <HolderGroup title="Has it" rows={result.holding} cap={cap} onToggle={toggle} />
        <HolderGroup title="Does not have it" rows={result.without} cap={cap} onToggle={toggle} />

        <Compare roles={roles} drafts={drafts} />
      </div>
    </div>
  );
}
